import CloseIcon from "@mui/icons-material/Close";
import { Box, Drawer, IconButton, Typography, useMediaQuery } from "@mui/material";
import { createContext, type ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

/** Combined filters host selections inline instead of opening nested modal drawers. */
export const InlineSelectionContext = createContext(false);

interface SelectionSheetProps {
  id?: string;
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  fullHeight?: boolean;
}

export function SelectionSheet({
  id,
  open,
  title,
  onClose,
  children,
  footer,
  fullHeight = false,
}: SelectionSheetProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [viewport, setViewport] = useState<{ height: number; bottom: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    const visual = window.visualViewport;
    const update = () =>
      setViewport({
        height: visual?.height ?? window.innerHeight,
        bottom: visual ? Math.max(0, window.innerHeight - visual.height - visual.offsetTop) : 0,
      });
    update();
    visual?.addEventListener("resize", update);
    visual?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      visual?.removeEventListener("resize", update);
      visual?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [open]);
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      transitionDuration={reducedMotion ? 0 : undefined}
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      slotProps={{
        paper: {
          id,
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": titleId,
          sx: {
            borderRadius: "16px 16px 0 0",
            width: "100%",
            maxWidth: "100%",
            height: fullHeight ? "100%" : "auto",
            maxHeight: viewport ? `${viewport.height * 0.9}px` : "90dvh",
            bottom: viewport?.bottom ?? 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            paddingBottom: "env(safe-area-inset-bottom)",
            paddingInline: "max(0px, env(safe-area-inset-left), env(safe-area-inset-right))",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography
          id={titleId}
          component="h2"
          variant="h6"
          sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}
        >
          {title}
        </Typography>
        <IconButton type="button" size="small" aria-label={t("common.close")} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", overscrollBehavior: "contain" }}>
        {children}
      </Box>
      {footer && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", flexShrink: 0 }}>{footer}</Box>
      )}
    </Drawer>
  );
}
